import { FC, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useParams } from 'next/navigation';
import {
  CatButton,
  CatDrawer,
  CatHeading,
  CatIconArrowForward,
  CatIconProp65,
  CatList,
  CatListItem,
  CatTextLink,
  CatTextPassage,
  CatTooltip
} from 'components/blocks-components';
import { prop65WarningMsg } from './utils';
import { getSeoUrl } from 'utils/links';
import { ProductDealerPriceAvailability } from 'services/dealerPriceAvailability/types';
import Price from 'components/price';
import { CATCORP_ESITE } from 'constants/common';
import {
  getPaymentMethods,
  revalidateCart
} from 'services/parts-diagram/actions';
import { useQuickToast } from 'hooks/use-toast';
import { useAddToCartEvent } from 'hooks/analytics/use-add-to-cart-event';
import {
  Conditional,
  DrawerBody,
  DrawerHeader,
  Portal,
  useBreakpoint,
  useDrawer
} from '@cat-ecom/pcc-components';
import { PAGE_BREAKPOINTS } from '@cat-ecom/pcc-components/src/constants/commonConstants';
import { DealerData } from 'types/parts-diagram';
import { ROOT_PORTALS } from 'constants/targets';
import { useCoreContext } from 'providers/core-provider';
import { getPartNumberForAddToCart } from 'services/cart/utils';
import { useEsiteCtx } from 'services/esite/context';
import styles from './product-quick-view-drawer.module.scss';
export interface ProductQuickViewDrawerProps {
  data: Record<string, unknown>;
  priceData: ProductDealerPriceAvailability;
  dealerData?: DealerData;
  currency: string;
  model?: string;
  listEvent?: string;
  drawerActive: boolean;
  setDrawerActive: (drawerActive: boolean) => void;
  isHeadlessPDPEnabled?: boolean;
  isPccReleaseRemanReplacement?: boolean;
  isPccPdpHeaderUpdateMobile?: boolean;
  ssoRedirectionUrl?: string;
  isRTL?: boolean;
  currencyLdFeatureFlag?: boolean;
  isPartDiagram?: boolean;
}

const ProductQuickViewDrawer: FC<ProductQuickViewDrawerProps> = ({
  data,
  priceData,
  dealerData,
  currency,
  model,
  listEvent,
  drawerActive,
  setDrawerActive,
  isHeadlessPDPEnabled,
  isPccReleaseRemanReplacement = false,
  isPccPdpHeaderUpdateMobile = false,
  ssoRedirectionUrl,
  isRTL = false,
  currencyLdFeatureFlag = false,
  isPartDiagram = false
}) => {
  const t = useTranslations();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  // TODO - Input Validation - Ensure that all values used from params are allowed values:
  // lang should always be a two-digit string without numbers or special characters (like 'es');
  // esite should always be a string without numbers or special characters, except for the dash (like demo-dealer).
  const params = useParams();
  const esiteUrl = getSeoUrl(params);
  const toast = useQuickToast();
  const isCatcorp = params.esite === CATCORP_ESITE;
  const isMobile = useBreakpoint(PAGE_BREAKPOINTS.MD);

  const { productCount = 0, products = [] } = data;
  const product = (productCount !== 0 && products[0]) || [];
  const {
    partNumber = '',
    name = '',
    prop65 = '',
    imageURL = '',
    attributes = [],
    shortDescription = ''
  } = product;
  const partItem = { ...priceData, partName: name };
  const { storeId, locale } = useEsiteCtx();
  const { fireEvent: fireEventAddToCart } = useAddToCartEvent();
  const { handleAddToCart } = useCoreContext();
  const { dealerName, id: dealerId } = dealerData || {};

  const productLink =
    ssoRedirectionUrl ||
    (isHeadlessPDPEnabled ? partNumber : `${esiteUrl}/${partNumber}`);

  const onClickHandler = async () => {
    setDrawerActive(false);
    // TODO - Input Validation - Ensure that all values passed to the service are allowed values:
    // part should be an object with the following properties:
    // partNumber should always be a string restricting generalRestrictedCharacters (@\constants\securityConstants.ts).
    // partName should always be a string restricting generalRestrictedCharacters (@\constants\securityConstants.ts).
    // quantity should always be a number.
    // sosCode should always be an alphanumeric string.
    const items = [
      {
        partNumber: getPartNumberForAddToCart(
          partNumber,
          partItem.sosCode,
          partItem.isCatPart
        ),
        partName: name,
        quantity: partItem.quantity,
        sosCode: partItem.sosCode,
        isCatPart: partItem.isCatPart
      }
    ];
    const { error, response } = await handleAddToCart(
      { items },
      storeId,
      locale
    );

    if (error) {
      toast.error({
        message: t('ITEM_ADD_ERROR'),
        className: styles['cat-c-toast']
      });
    } else {
      toast.success({
        message: t('ITEM_ADDED_TO_CART'),
        className: styles['cat-c-toast']
      });
    }
    const { orderId } = response;
    const { paymentMethods } = await getPaymentMethods(orderId);

    if (paymentMethods) {
      fireEventAddToCart({
        cartId: orderId,
        currencyCode: currency,
        dealerId,
        model,
        product: partItem,
        storeName: dealerName,
        paymentMethods,
        list: listEvent
      });
    }
  };

  const productPrice = useMemo(() => {
    return (
      !isCatcorp && (
        <Price
          price={priceData}
          currency={currency}
          isRTL={isRTL}
          currencyLdFeatureFlag={currencyLdFeatureFlag}
        />
      )
    );
  }, [isCatcorp, priceData, currency, isRTL, currencyLdFeatureFlag]);

  const handleCloseDrawer = () => {
    setDrawerActive(false);
  };

  const { drawerProps, openDrawer } = useDrawer();

  const showAddToCartButton = isPartDiagram
    ? partItem?.replacementParts?.length === 0
    : true;

  return (
    <Portal id={ROOT_PORTALS}>
      <CatDrawer
        isActive={drawerActive}
        data-testid="product-quick-view-drawer"
        onBlDrawerClose={handleCloseDrawer}
      >
        <div slot="header" data-testid="product-quick-view-header">
          {productCount !== 0 ? (
            <CatHeading slot="header" variant="title-sm">
              {`${partNumber}: ${name}`}
            </CatHeading>
          ) : (
            <p>{t('FYD_NO_RESULTS')}</p>
          )}
        </div>
        {productCount !== 0 && (
          <>
            <div className="d-flex flex-column row-gap-2">
              <div className="d-flex justify-content-center align-items-center">
                <Image
                  src={imageURL}
                  alt={partNumber}
                  loading="lazy"
                  width={220}
                  height={216}
                  unoptimized
                />
              </div>
              <div className="d-flex flex-column row-gap-2">
                <p className="cat-u-theme-typography-body-sm">
                  {shortDescription}
                </p>
                {prop65 && (
                  <Conditional
                    fallback={
                      <CatTooltip
                        isInteractive
                        position={isMobile ? 'right' : 'top'}
                      >
                        <div
                          slot="trigger"
                          className="d-flex column-gap-1 align-items-center"
                        >
                          <CatIconProp65 />
                          <CatTextLink size="xs">
                            {t('PRODUCTS_WARNINGS_SIMPLE')}
                          </CatTextLink>
                        </div>
                        <CatTextPassage
                          size="xs"
                          className={styles['product-warning-tooltip-msg']}
                        >
                          <b>{t('INACTIVITY_WARNING_DIALOG_WARNING_ICON')}:</b>
                          <span
                            className="cat-u-theme-typography-footnote-bold cat-u-padding-left-sm"
                            dangerouslySetInnerHTML={{
                              __html: prop65WarningMsg(prop65)
                            }}
                          />
                        </CatTextPassage>
                      </CatTooltip>
                    }
                    test={
                      (isPccReleaseRemanReplacement ||
                        isPccPdpHeaderUpdateMobile) &&
                      !isMD
                    }
                  >
                    <div
                      slot="trigger"
                      className="d-flex column-gap-1 align-items-center"
                    >
                      <CatIconProp65 />
                      <CatTextLink className="fs-4" onClick={openDrawer}>
                        {t('PRODUCTS_WARNINGS_SIMPLE')}
                      </CatTextLink>
                    </div>
                    <CatDrawer
                      {...drawerProps}
                      orientation="bottom"
                      className={styles['product-warning-drawer']}
                      data-testid="product-warning-drawer"
                      verticalMaxHeight="large"
                    >
                      <DrawerHeader className="d-flex justify-content-between">
                        <CatHeading variant="title-sm">
                          {t('PRODUCTS_WARNINGS_SIMPLE')}
                        </CatHeading>
                      </DrawerHeader>
                      <DrawerBody>
                        <CatTextPassage
                          className={styles['product-warning-tooltip-msg']}
                        >
                          <b>{t('INACTIVITY_WARNING_DIALOG_WARNING_ICON')}:</b>
                          <span
                            className="fs-4 fw-normal lh-base"
                            dangerouslySetInnerHTML={{
                              __html: prop65WarningMsg(prop65)
                            }}
                          />
                        </CatTextPassage>
                      </DrawerBody>
                    </CatDrawer>
                  </Conditional>
                )}

                {productPrice}

                {attributes?.length === 0 ? (
                  <p className="cat-u-theme-typography-body-sm">
                    {t('KITS_NO_SPECS_AVAILABLE')}
                  </p>
                ) : (
                  <CatList spacing="condensed" className={styles['cat-c-list']}>
                    {attributes?.map((a: Record<string, string>) => (
                      <CatListItem key={a.name} isStatic>
                        <p className="cat-u-margin-bottom-none cat-u-theme-typography-body-sm">
                          <strong>{`${a.name}: `}</strong>
                          {a.value}
                        </p>
                      </CatListItem>
                    ))}
                  </CatList>
                )}
              </div>
            </div>
            <div slot="footer" data-testid="product-quick-view-footer">
              {!isCatcorp &&
                priceData?.enableAddToCart &&
                showAddToCartButton && (
                  <CatButton
                    variant="primary"
                    onClick={() => {
                      onClickHandler();
                      revalidateCart();
                    }}
                  >
                    {t('ADD_TO_CART')}
                  </CatButton>
                )}
              <CatButton
                variant="ghost"
                href={productLink}
                data-track-event="e_primaryCTA"
                data-track-attr-nltext="view details"
                data-track-attr-context="Product preview drawer footer section"
              >
                {t('DETAILS')}
                <CatIconArrowForward slot="after" />
              </CatButton>
            </div>
          </>
        )}
      </CatDrawer>
    </Portal>
  );
};

export default ProductQuickViewDrawer;
